import React from "react";

/**
 * A forecast range as a move, for the research listings.
 *
 * Measured from the price the model itself found, falling back to the baseline
 * entered at settlement. That order is deliberate: a range is a claim about a
 * move, and the move a model was claiming is relative to where it thought price
 * was. Reading its range against a baseline it never saw describes something it
 * did not say.
 *
 * It also means the figure is there from the moment an answer is pasted. The
 * baseline now arrives at settlement, which can be a month later, and keying
 * off it left this column blank for the whole life of a forecast — which is
 * most of the time anyone is looking at one.
 *
 * Lived in two files with the same body. Changing the same logic in two places
 * twice was enough; it lives here now.
 */

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const signed = (n) => `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;

const RangePercent = ({ row }) => {
  const from = num(row.model_reported_price) ?? num(row.current_price);
  const lo = num(row.range_min);
  const hi = num(row.range_max);
  if (!from || lo === null || hi === null) return null;

  const lowPct = ((Math.min(lo, hi) - from) / from) * 100;
  const highPct = ((Math.max(lo, hi) - from) / from) * 100;

  // Which way the range points, decided by the server and served with the row.
  // This used to be judged here, from whether one end was half again as far
  // out as the other -- a third rule, on top of the scorer's and the verdict
  // badge's, and one that disagreed with them: a band of -4.1% to +4.8% is not
  // lopsided by that measure and stayed grey, while the same row's verdict
  // called it up. The rule that matters is whether the band's lean clears what
  // trading it would cost, and it lives in memberScoreService.
  const styles = {
    up: { color: "text-success", arrow: "ri-arrow-up-line" },
    down: { color: "text-danger", arrow: "ri-arrow-down-line" },
  };
  const { color = "text-muted", arrow = "ri-arrow-right-line" } =
    styles[row.member_direction] || {};

  return (
    <span
      className={`${color} fs-12 d-block`}
      title={
        num(row.model_reported_price) !== null
          ? `From the ${from} this model found for itself`
          : `From the ${from} recorded when this forecast was settled`
      }
    >
      <i className={`${arrow} align-bottom`} /> {signed(lowPct)} to {signed(highPct)}
    </span>
  );
};

export default RangePercent;
